/**
 * Fee Templates by Modality
 *
 * Defines standard fee structures for different shipping modalities.
 * Each fee has a category (origin/freight/destination/additional) for organization.
 */

export type FeeCategory = 'origin' | 'freight' | 'destination' | 'additional'

export interface FeeTemplate {
  id: string
  name: string
  category: FeeCategory
  description?: string
  defaultSelected?: boolean // Auto-select this fee
}

/**
 * Maritime Shipping Fees (FCL and LCL)
 */
export const MARITIME_FEES: FeeTemplate[] = [
  // Origin Fees
  {
    id: 'maritime-freight',
    name: 'Frete Marítimo',
    category: 'freight',
    description: 'Frete internacional marítimo',
    defaultSelected: true,
  },
  {
    id: 'thc-origin',
    name: 'THC Origem',
    category: 'origin',
    description: 'Terminal Handling Charge na origem',
    defaultSelected: true,
  },
  {
    id: 'bl-fee',
    name: 'BL Fee',
    category: 'origin',
    description: 'Taxa de emissão do Bill of Lading',
    defaultSelected: true,
  },
  {
    id: 'isps-origin',
    name: 'ISPS Origem',
    category: 'origin',
    description: 'International Ship and Port Security',
  },
  {
    id: 'vgm',
    name: 'VGM',
    category: 'origin',
    description: 'Verified Gross Mass',
  },
  {
    id: 'doc-fee-origin',
    name: 'Doc Fee Origem',
    category: 'origin',
    description: 'Taxa de documentação na origem',
  },

  // Destination Fees
  {
    id: 'thc-destination',
    name: 'THC Destino',
    category: 'destination',
    description: 'Terminal Handling Charge no destino',
    defaultSelected: true,
  },
  {
    id: 'isps-destination',
    name: 'ISPS Destino',
    category: 'destination',
    description: 'International Ship and Port Security',
  },
  {
    id: 'doc-fee-destination',
    name: 'Doc Fee Destino',
    category: 'destination',
    description: 'Taxa de documentação no destino',
  },
  {
    id: 'demurrage',
    name: 'Demurrage',
    category: 'destination',
    description: 'Taxa de permanência do container',
  },

  // Additional Fees
  {
    id: 'baf',
    name: 'BAF - Bunker Adjustment Factor',
    category: 'additional',
    description: 'Ajuste de combustível',
  },
  {
    id: 'caf',
    name: 'CAF - Currency Adjustment Factor',
    category: 'additional',
    description: 'Ajuste cambial',
  },
  {
    id: 'war-risk',
    name: 'War Risk',
    category: 'additional',
    description: 'Seguro de risco de guerra',
  },
  {
    id: 'seal-fee',
    name: 'Seal Fee',
    category: 'additional',
    description: 'Taxa de lacre',
  },
  {
    id: 'inspection',
    name: 'Inspeção',
    category: 'additional',
    description: 'Taxa de inspeção',
  },
]

/**
 * Air Shipping Fees
 */
export const AIR_FEES: FeeTemplate[] = [
  // Freight
  {
    id: 'air-freight',
    name: 'Frete Aéreo',
    category: 'freight',
    description: 'Frete internacional aéreo',
    defaultSelected: true,
  },

  // Origin Fees
  {
    id: 'awb-fee',
    name: 'AWB Fee',
    category: 'origin',
    description: 'Taxa de emissão do Air Waybill',
    defaultSelected: true,
  },
  {
    id: 'handling-origin',
    name: 'Handling Origem',
    category: 'origin',
    description: 'Taxa de manuseio na origem',
    defaultSelected: true,
  },
  {
    id: 'security-surcharge',
    name: 'Security Surcharge',
    category: 'origin',
    description: 'Taxa de segurança',
  },
  {
    id: 'doc-fee-origin',
    name: 'Doc Fee Origem',
    category: 'origin',
    description: 'Taxa de documentação na origem',
  },

  // Destination Fees
  {
    id: 'handling-destination',
    name: 'Handling Destino',
    category: 'destination',
    description: 'Taxa de manuseio no destino',
    defaultSelected: true,
  },
  {
    id: 'doc-fee-destination',
    name: 'Doc Fee Destino',
    category: 'destination',
    description: 'Taxa de documentação no destino',
  },

  // Additional Fees
  {
    id: 'fuel-surcharge',
    name: 'Fuel Surcharge',
    category: 'additional',
    description: 'Taxa de combustível',
  },
  {
    id: 'peak-season',
    name: 'Peak Season',
    category: 'additional',
    description: 'Taxa de alta temporada',
  },
  {
    id: 'dangerous-goods',
    name: 'Dangerous Goods Fee',
    category: 'additional',
    description: 'Taxa para carga perigosa',
  },
]

/**
 * Ground/Terrestrial Shipping Fees
 */
export const GROUND_FEES: FeeTemplate[] = [
  // Freight
  {
    id: 'ground-freight',
    name: 'Frete Terrestre',
    category: 'freight',
    description: 'Frete internacional terrestre',
    defaultSelected: true,
  },

  // Origin Fees
  {
    id: 'pickup-fee',
    name: 'Taxa de Coleta',
    category: 'origin',
    description: 'Taxa de coleta na origem',
    defaultSelected: true,
  },
  {
    id: 'loading-fee',
    name: 'Taxa de Carregamento',
    category: 'origin',
    description: 'Taxa de carregamento',
  },
  {
    id: 'doc-fee-origin',
    name: 'Doc Fee Origem',
    category: 'origin',
    description: 'Taxa de documentação na origem',
  },

  // Destination Fees
  {
    id: 'delivery-fee',
    name: 'Taxa de Entrega',
    category: 'destination',
    description: 'Taxa de entrega no destino',
    defaultSelected: true,
  },
  {
    id: 'unloading-fee',
    name: 'Taxa de Descarregamento',
    category: 'destination',
    description: 'Taxa de descarregamento',
  },
  {
    id: 'doc-fee-destination',
    name: 'Doc Fee Destino',
    category: 'destination',
    description: 'Taxa de documentação no destino',
  },

  // Additional Fees
  {
    id: 'toll-fees',
    name: 'Pedágios',
    category: 'additional',
    description: 'Taxa de pedágios',
  },
  {
    id: 'escort-fee',
    name: 'Escolta',
    category: 'additional',
    description: 'Taxa de escolta',
  },
  {
    id: 'insurance',
    name: 'Seguro',
    category: 'additional',
    description: 'Seguro de carga',
  },
]

/**
 * Get fee templates by modality name
 */
export function getFeeTemplatesByModality(modalityName: string): FeeTemplate[] {
  const normalized = modalityName.toLowerCase()

  if (normalized.includes('mar') || normalized.includes('sea')) {
    return MARITIME_FEES
  }

  if (normalized.includes('aer') || normalized.includes('air')) {
    return AIR_FEES
  }

  if (normalized.includes('ter') || normalized.includes('rod') || normalized.includes('ground')) {
    return GROUND_FEES
  }

  // Default: maritime fees
  return MARITIME_FEES
}

/**
 * Get default selected fees for a modality
 */
export function getDefaultFees(modalityName: string): FeeTemplate[] {
  const templates = getFeeTemplatesByModality(modalityName)
  return templates.filter(fee => fee.defaultSelected)
}

/**
 * Get fees by category
 */
export function getFeesByCategory(fees: FeeTemplate[], category: FeeCategory): FeeTemplate[] {
  return fees.filter(fee => fee.category === category)
}

/**
 * Category labels in Portuguese
 */
export const CATEGORY_LABELS: Record<FeeCategory, string> = {
  origin: 'Taxas de Origem',
  freight: 'Frete Internacional',
  destination: 'Taxas de Destino',
  additional: 'Taxas Adicionais',
}

/**
 * Category descriptions
 */
export const CATEGORY_DESCRIPTIONS: Record<FeeCategory, string> = {
  origin: 'Taxas cobradas no porto/aeroporto de origem',
  freight: 'Custo do transporte internacional',
  destination: 'Taxas cobradas no porto/aeroporto de destino',
  additional: 'Sobretaxas e serviços extras',
}
